import { RcFile } from "antd/lib/upload";
import {
    AnalysisOutput,
    Project,
    ProjectSummary,
    ProjectAnalysis,
    ProjectAnalysisUpdateRequest,
    ProjectCreateRequest,
    ProjectSourceType,
    ProjectStatus,
    ProjectType,
    ProjectUpdateRequest,
    SaveAnalysisFeedbackRequest,
    SurveySchema,
} from "../../types/ProjectTypes";
import { Document } from "../../types/Document";
import { geturl, safeDELETE, safeFormPOST, safeFormPUT, safeGET, safePOST, safePUT, safeTextGET } from "./SafeClient";

export function listProjects(statusFilter?: ProjectStatus[], abort?: any): Promise<ProjectSummary[]> {
    let url = "/api/project";

    if (statusFilter && statusFilter.length > 0) {
        const statusParams = statusFilter.map((s) => `status[]=${s}`).join("&");
        url += `?${statusParams}`;
    }

    return safeGET(url, abort);
}

export function getProject(projectId: number, abort?: any): Promise<Project> {
    return safeGET(`/api/project/${projectId}`, abort);
}

export function createProject(request: ProjectCreateRequest, abort?: any): Promise<Project> {
    const { files, ...data } = request;
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    if (files) {
        for (const file of files) {
            formData.append("files", file);
        }
    }
    return safeFormPOST("/api/project", formData, abort);
}

export function updateProject(
    projectId: number,
    updates: ProjectUpdateRequest,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}`, updates, abort);
}

export function renameProject(
    projectId: number,
    name: string,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/name`, { name }, abort);
}

export function setProjectCategory(
    projectId: number,
    categoryPath: string[] | null,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/category`, { categoryPath }, abort);
}

export function reassignProjectOwner(
    projectId: number,
    userId: number,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/owner/${userId}`, {}, abort);
}

export function reassignProjectGroup(
    projectId: number,
    groupId: number,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/group/${groupId}`, {}, abort);
}

export function deleteProject(projectId: number, abort?: any): Promise<{ success: boolean }> {
    return safeDELETE(`/api/project/${projectId}`, undefined, abort);
}

export interface AnalysisInputRequest {
    survey: { schema: object; response: object };
    removedDocumentIds: number[];
    files: File[];
}

export function updateAnalysis(
    projectId: number,
    request: AnalysisInputRequest,
    abort?: any
): Promise<Project> {
    return safeFormPUT(
        `/api/project/${projectId}/analysis/update`,
        buildAnalysisFormData(request),
        abort
    );
}

export function submitAnalysis(
    projectId: number,
    request?: AnalysisInputRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    const url = `/api/project/${projectId}/analysis/submit`;
    if (request) {
        return safeFormPOST(url, buildAnalysisFormData(request), abort);
    }
    return safePOST(url, {}, abort);
}

function buildAnalysisFormData(request: AnalysisInputRequest): FormData {
    const { files, ...data } = request;
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    for (const file of files) {
        formData.append("files", file);
    }
    return formData;
}

export function getSurveySchema(
    projectId: number,
    abort?: any
): Promise<SurveySchema> {
    return safeGET(`/api/project/${projectId}/survey-schema`, abort);
}

export function getInitialSurveySchema(
    projectType: ProjectType,
    options?: { source?: ProjectSourceType },
    abort?: any
): Promise<SurveySchema> {
    const qs = options?.source
        ? `?source=${encodeURIComponent(options.source)}`
        : "";
    return safeGET(`/api/project/survey-schema/${projectType}${qs}`, abort);
}

export function updateProjectAnalysis(
    projectId: number,
    analysisVersion: number,
    updates: ProjectAnalysisUpdateRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePUT(`/api/project/${projectId}/analysis/${analysisVersion}`, updates, abort);
}

export function getAnalysisOutput(
    projectId: number,
    analysisVersion: number,
    abort?: any
): Promise<AnalysisOutput> {
    return safeGET(`/api/project/${projectId}/analysis/${analysisVersion}/output`, abort);
}

export function getAnalysisOutputContent(
    projectId: number,
    analysisVersion: number,
    abort?: any
): Promise<string> {
    return safeTextGET(`/api/project/${projectId}/analysis/${analysisVersion}/output/content`, abort);
}

export function requestAnalysisChange(
    projectId: number,
    message: string,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(
        `/api/project/${projectId}/analysis/request-change`,
        { message },
        abort
    );
}

export function saveAnalysisOutput(
    projectId: number,
    analysisVersion: number,
    request: { notify: boolean; output: AnalysisOutput },
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(`/api/project/${projectId}/analysis/${analysisVersion}/output`, request, abort);
}

export function saveAnalysisFeedback(
    projectId: number,
    analysisVersion: number,
    request: SaveAnalysisFeedbackRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(
        `/api/project/${projectId}/analysis/${analysisVersion}/feedback`,
        request,
        abort
    );
}

export function removeAnalysisFeedback(
    projectId: number,
    analysisVersion: number,
    path: string,
    abort?: any
): Promise<ProjectAnalysis> {
    return safeDELETE(
        `/api/project/${projectId}/analysis/${analysisVersion}/feedback/${encodeURIComponent(path)}`,
        undefined,
        abort
    );
}

// ── Attachment endpoints ──────────────────────────────────────────────

export function uploadAnalysisAttachment(
    projectId: number,
    analysisVersion: number,
    file: RcFile,
    abort?: any
): Promise<Document> {
    const formData = new FormData();
    formData.append("file", file);

    return safeFormPOST(
        `/api/project/${projectId}/analysis/${analysisVersion}/attachment`,
        formData,
        abort
    );
}

export function getAttachmentDownloadUrl(
    projectId: number,
    analysisVersion: number,
    documentId: number
): string {
    // Used as a plain <a href>, so it must point at the API host explicitly:
    // the session cookie lives there, and a bare path would resolve against
    // the SPA origin where the request arrives without credentials.
    return geturl(`/api/project/${projectId}/analysis/${analysisVersion}/attachment/${documentId}`);
}

export function deleteAnalysisAttachment(
    projectId: number,
    analysisVersion: number,
    documentId: number,
    abort?: any
): Promise<{ success: boolean }> {
    return safeDELETE(
        `/api/project/${projectId}/analysis/${analysisVersion}/attachment/${documentId}`,
        undefined,
        abort
    );
}

export function setCollaborator(
    projectId: number,
    userId: number,
    canEdit: boolean,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/collaborator/${userId}`, { canEdit }, abort);
}

export function removeCollaborator(
    projectId: number,
    userId: number,
    abort?: any
): Promise<Project> {
    return safeDELETE(`/api/project/${projectId}/collaborator/${userId}`, undefined, abort);
}
